import {
  FaBookQuran,
  FaSitemap,
  FaBullhorn,
  FaDoorOpen,
  FaHeartPulse,
  FaTrophy,
  FaUserPlus,
  FaUsers,
} from "react-icons/fa6";

import type { FeatureModule } from "./types";

export const kesiswaanModules: FeatureModule[] = [
  {
    slug: "prestasi-pelanggaran",
    label: "Prestasi & Pelanggaran",
    navDescription: "Poin pembinaan karakter",
    icon: FaTrophy,
    group: "kesiswaan",
    summary:
      "Catat prestasi dan pelanggaran siswa beserta progres pembinaannya, terbuka untuk orang tua.",
    detail: {
      title: "Pembinaan karakter yang *punya catatan*, bukan hanya ingatan",
      lead: "Prestasi, pelanggaran, dan progres pembinaan tercatat dan terbaca orang tua",
      intro:
        "Selama ini catatan pelanggaran tersimpan di buku wali kelas dan prestasi hanya diingat saat ada lomba. Ketika keduanya dicatat di sistem yang sama, pembinaan berhenti bergantung pada siapa yang kebetulan ingat — dan orang tua tahu perkembangan anaknya tanpa harus dipanggil ke sekolah.",
      highlights: [
        "Prestasi dan pelanggaran tercatat dengan waktu dan keterangannya",
        "Progres kegiatan pembinaan ikut terpantau",
        "Terbuka untuk orang tua lewat aplikasi",
      ],
      benefits: [
        {
          title: "Tidak bergantung pada satu guru",
          body: "Catatan tersimpan di sistem, bukan di buku pribadi wali kelas. Ketika wali kelas berganti, riwayat pembinaan seorang anak tetap utuh.",
        },
        {
          title: "Prestasi tidak menguap",
          body: "Pencapaian siswa terdokumentasi sepanjang tahun, jadi ada dasar saat sekolah menyusun rekomendasi atau memilih perwakilan lomba.",
        },
        {
          title: "Orang tua ikut membina",
          body: "Karena catatan terbuka di aplikasi, orang tua tahu lebih awal dan bisa ikut menangani — bukan baru diberi tahu setelah masalahnya membesar.",
        },
        {
          title: "Progres yang bisa dilihat",
          body: "Modul progres kegiatan memperlihatkan perkembangan pembinaan, sehingga perbaikan perilaku terlihat sebagai proses, bukan satu kejadian.",
        },
      ],
      screensTitle: "Tiga catatan yang saling melengkapi",
      screensLead:
        "Orang tua membuka ketiganya dari menu Kesiswaan di aplikasi CARDS.",
      screens: [
        {
          src: "/image/fitur/prestasi-pelanggaran/mobile-prestasi.webp",
          alt: "Daftar prestasi siswa di aplikasi CARDS",
          caption: "Prestasi",
          note: "Pencapaian siswa sepanjang tahun ajaran",
          kind: "mobile",
        },
        {
          src: "/image/fitur/prestasi-pelanggaran/mobile-pelanggaran.webp",
          alt: "Catatan pelanggaran siswa di aplikasi CARDS",
          caption: "Pelanggaran",
          note: "Catatan beserta waktu dan keterangannya",
          kind: "mobile",
        },
        {
          src: "/image/fitur/prestasi-pelanggaran/mobile-progres.webp",
          alt: "Progres kegiatan pembinaan siswa di aplikasi CARDS",
          caption: "Progres",
          note: "Perkembangan pembinaan yang berjalan",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah semua pelanggaran langsung terlihat orang tua?",
          a: "Keterbukaan catatan mengikuti kebijakan sekolah. Yang pasti, catatan tersimpan di sistem sehingga penanganannya tidak bergantung pada ingatan seorang guru.",
        },
        {
          q: "Apakah ada sistem poin?",
          a: "Bobot dan tindak lanjut atas prestasi maupun pelanggaran disesuaikan dengan aturan pembinaan sekolah Anda pada tahap implementasi.",
        },
        {
          q: "Siapa yang boleh menambahkan catatan?",
          a: "Akses menambah catatan diatur lewat pengaturan pengguna, sehingga hanya peran yang Anda beri izin — misalnya wali kelas atau guru BK — yang dapat mengisinya.",
        },
      ],
      metaTitle: "Catatan Prestasi & Pelanggaran Siswa",
      metaDescription:
        "Catat prestasi, pelanggaran, dan progres pembinaan siswa dalam satu sistem yang terbuka untuk orang tua lewat aplikasi CARDS.",
      keywords: [
        "catatan pelanggaran siswa",
        "poin pelanggaran sekolah",
        "prestasi siswa digital",
        "pembinaan karakter siswa",
        "aplikasi kesiswaan sekolah",
      ],
    },
  },

  {
    slug: "perizinan-kunjungan",
    label: "Perizinan & Kunjungan",
    navDescription: "Izin keluar & kunjungan wali",
    icon: FaDoorOpen,
    group: "kesiswaan",
    summary:
      "Pengajuan izin siswa dan pencatatan kunjungan wali, lengkap dengan persetujuannya.",
    detail: {
      title: "Izin dan kunjungan yang *tercatat, bukan lewat pesan singkat*",
      lead: "Pengajuan izin siswa dan kunjungan wali punya alur resmi beserta riwayatnya",
      intro:
        "Izin yang disampaikan lewat pesan ke wali kelas mudah hilang di antara ratusan chat lain, dan kunjungan wali sering hanya dicatat di buku tamu yang jarang dibuka lagi. Kedua hal ini dipindahkan ke alur resmi supaya sekolah tahu persis siapa yang keluar, kapan, dan atas izin siapa.",
      highlights: [
        "Pengajuan izin lewat aplikasi, bukan pesan pribadi",
        "Kunjungan wali tercatat beserta waktunya",
        "Riwayat yang bisa ditelusuri saat dibutuhkan",
      ],
      benefits: [
        {
          title: "Izin tidak tenggelam di chat",
          body: "Pengajuan masuk sebagai data, bukan pesan yang harus dicari ulang di antara percakapan lain saat wali kelas perlu memastikan sesuatu.",
        },
        {
          title: "Sekolah tahu siapa yang keluar",
          body: "Setiap izin keluar tercatat lengkap dengan waktunya, penting bagi sekolah berasrama maupun sekolah yang menerapkan pengawasan gerbang.",
        },
        {
          title: "Kunjungan wali terdokumentasi",
          body: "Kedatangan orang tua atau wali dicatat sistematis, sehingga sekolah punya riwayat kunjungan yang dapat ditelusuri kembali.",
        },
        {
          title: "Menyatu dengan data kesiswaan",
          body: "Perizinan dan kunjungan berada dalam satu modul dengan catatan kesiswaan lain, jadi gambaran tentang seorang siswa tidak terpecah-pecah.",
        },
      ],
      screensTitle: "Dua alur di menu Kesiswaan",
      screensLead:
        "Orang tua mengajukan dan menelusuri keduanya dari aplikasi yang sama.",
      screens: [
        {
          src: "/image/fitur/perizinan-kunjungan/mobile-perizinan.webp",
          alt: "Pengajuan dan riwayat perizinan siswa di aplikasi CARDS",
          caption: "Perizinan",
          note: "Pengajuan izin beserta riwayatnya",
          kind: "mobile",
        },
        {
          src: "/image/fitur/perizinan-kunjungan/mobile-kunjungan.webp",
          alt: "Catatan kunjungan wali di aplikasi CARDS",
          caption: "Kunjungan",
          note: "Riwayat kunjungan wali ke sekolah",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah izin harus disetujui dulu sebelum berlaku?",
          a: "Alur persetujuan mengikuti kebijakan sekolah. Yang pasti, setiap pengajuan tercatat sehingga statusnya jelas dan dapat ditelusuri.",
        },
        {
          q: "Apakah modul ini cocok untuk pesantren?",
          a: "Sangat cocok. Izin keluar asrama dan kunjungan wali santri adalah kebutuhan sehari-hari pesantren, dan keduanya ditangani modul ini.",
        },
        {
          q: "Apa bedanya dengan Buku Tamu?",
          a: "Kunjungan mencatat kedatangan wali terkait siswa tertentu, sedangkan Buku Tamu mencatat tamu sekolah secara umum di modul Pengumuman & Buku Tamu.",
        },
      ],
      metaTitle: "Perizinan Siswa & Kunjungan Wali Online",
      metaDescription:
        "Pengajuan izin siswa dan pencatatan kunjungan wali lewat alur resmi di aplikasi, lengkap dengan riwayat yang dapat ditelusuri sekolah dan pesantren.",
      keywords: [
        "perizinan siswa online",
        "izin keluar santri",
        "kunjungan wali santri",
        "aplikasi perizinan sekolah",
        "izin siswa digital",
      ],
    },
  },

  {
    slug: "kesehatan-perundungan",
    label: "Kesehatan & Perundungan",
    navDescription: "Catatan UKS & pelaporan",
    icon: FaHeartPulse,
    group: "kesiswaan",
    summary:
      "Rekam kondisi kesehatan siswa dan sediakan kanal pelaporan perundungan yang tertangani.",
    detail: {
      title: "Dua hal yang *tidak boleh luput* dari perhatian sekolah",
      lead: "Catatan kesehatan siswa dan kanal pelaporan perundungan dalam satu modul kesiswaan",
      intro:
        "Riwayat kesehatan sering hanya diketahui petugas UKS, dan perundungan kerap baru terungkap setelah berlarut. Keduanya ditempatkan sebagai catatan resmi supaya sekolah punya data untuk bertindak — dan orang tua punya jalur untuk menyampaikan.",
      highlights: [
        "Catatan kondisi dan penanganan kesehatan siswa",
        "Kanal pelaporan perundungan yang tercatat",
        "Terbaca orang tua lewat menu Kesiswaan",
      ],
      benefits: [
        {
          title: "Riwayat kesehatan yang menetap",
          body: "Kondisi khusus, alergi, dan penanganan yang pernah dilakukan tersimpan di sistem — bukan hanya diketahui petugas UKS yang sedang bertugas.",
        },
        {
          title: "Perundungan punya jalur resmi",
          body: "Laporan masuk sebagai catatan yang tertelusuri, bukan keluhan lisan yang mudah berhenti di tengah jalan.",
        },
        {
          title: "Orang tua bisa menyampaikan",
          body: "Karena modul ini terbuka di aplikasi orang tua, kekhawatiran dapat disampaikan lebih awal tanpa harus menunggu pertemuan wali murid.",
        },
        {
          title: "Dasar untuk bertindak",
          body: "Catatan yang tersusun memberi sekolah pijakan objektif saat menentukan penanganan, alih-alih bergantung pada keterangan yang simpang siur.",
        },
      ],
      screensTitle: "Pelaporan dari sisi orang tua",
      screensLead:
        "Ditempatkan di menu Kesiswaan, sejajar dengan prestasi dan perizinan.",
      screens: [
        {
          src: "/image/fitur/kesehatan-perundungan/mobile-perundungan.webp",
          alt: "Pelaporan perundungan di aplikasi CARDS untuk orang tua",
          caption: "Perundungan",
          note: "Kanal pelaporan yang tercatat dan tertelusuri",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah laporan perundungan bisa disampaikan tanpa nama?",
          a: "Ketentuan kerahasiaan pelapor mengikuti kebijakan perlindungan anak di sekolah Anda, dan disesuaikan pada tahap implementasi.",
        },
        {
          q: "Siapa yang dapat membuka catatan kesehatan siswa?",
          a: "Akses diatur lewat pengaturan pengguna, sehingga hanya peran yang Anda beri izin — misalnya petugas UKS dan guru BK — yang dapat membukanya.",
        },
        {
          q: "Apakah catatan kesehatan terhubung dengan presensi?",
          a: "Keduanya berada dalam sistem yang sama, sehingga ketidakhadiran karena sakit dapat dibaca berdampingan dengan catatan kesehatan siswa.",
        },
      ],
      metaTitle: "Catatan Kesehatan Siswa & Pelaporan Perundungan",
      metaDescription:
        "Rekam kondisi dan penanganan kesehatan siswa, serta sediakan kanal pelaporan perundungan yang tercatat dan dapat ditindaklanjuti sekolah.",
      keywords: [
        "catatan kesehatan siswa",
        "uks digital sekolah",
        "pelaporan perundungan sekolah",
        "anti bullying sekolah",
        "aplikasi kesehatan siswa",
      ],
    },
  },

  {
    slug: "tahfidz",
    label: "Tahfidz",
    navDescription: "Setoran hafalan per surat & juz",
    icon: FaBookQuran,
    group: "kesiswaan",
    summary:
      "Pencatatan setoran hafalan santri per surat dan juz, dengan progres yang terpantau.",
    detail: {
      title: "Setoran hafalan yang *terekam per surat dan juz*",
      lead: "Progres tahfidz santri tercatat rapi dan bisa diikuti orang tua dari rumah",
      intro:
        "Buku setoran tahfidz mudah rusak, tertinggal, atau hilang — dan ketika itu terjadi, progres berbulan-bulan ikut hilang. Dengan pencatatan digital, setiap setoran tersimpan sehingga ustaz tahu sampai mana santri berjalan, dan orang tua bisa mengikutinya tanpa menunggu kunjungan.",
      highlights: [
        "Setoran tercatat per surat dan per juz",
        "Progres hafalan terpantau sepanjang waktu",
        "Orang tua mengikuti perkembangan dari aplikasi",
      ],
      benefits: [
        {
          title: "Tidak ada progres yang hilang",
          body: "Riwayat setoran tersimpan di sistem, jadi buku yang rusak atau tertinggal tidak lagi berarti kehilangan catatan berbulan-bulan.",
        },
        {
          title: "Ustaz tahu titik lanjutnya",
          body: "Capaian tiap santri terbaca jelas, sehingga penyimakan bisa langsung dimulai dari bagian yang tepat tanpa menerka-nerka.",
        },
        {
          title: "Orang tua ikut mendampingi",
          body: "Perkembangan hafalan terbuka di aplikasi, memungkinkan orang tua ikut memotivasi meski anak berada di asrama.",
        },
        {
          title: "Menyatu dengan data santri lain",
          body: "Tahfidz berada dalam modul kesiswaan yang sama dengan presensi kegiatan dan perizinan, jadi gambaran tentang santri tetap utuh.",
        },
      ],
      screensTitle: "Progres hafalan di aplikasi orang tua",
      screensLead:
        "Dibuka dari menu Kesiswaan, berdampingan dengan catatan santri lainnya.",
      screens: [
        {
          src: "/image/fitur/tahfidz/mobile-tahfidz.webp",
          alt: "Progres setoran hafalan tahfidz di aplikasi CARDS",
          caption: "Tahfidz",
          note: "Riwayat setoran hafalan per surat dan juz",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah metode penilaian setoran bisa disesuaikan?",
          a: "Komponen penilaian setoran disesuaikan dengan metode tahfidz yang dipakai pesantren Anda pada tahap implementasi.",
        },
        {
          q: "Bisakah satu santri disimak oleh beberapa ustaz?",
          a: "Pencatatan setoran mengikuti pengaturan pengguna sekolah, sehingga beberapa pengampu dapat diberi akses sesuai pembagian tugas Anda.",
        },
        {
          q: "Apakah modul ini hanya untuk pesantren?",
          a: "Tahfidz paling banyak dipakai pesantren dan sekolah Islam terpadu, namun tersedia bagi lembaga mana pun yang menjalankan program hafalan.",
        },
      ],
      metaTitle: "Aplikasi Tahfidz — Setoran Hafalan Santri",
      metaDescription:
        "Catat setoran hafalan santri per surat dan juz. Progres tahfidz terpantau ustaz dan dapat diikuti orang tua langsung dari aplikasi CARDS.",
      keywords: [
        "aplikasi tahfidz",
        "setoran hafalan santri",
        "pencatatan hafalan quran",
        "aplikasi pesantren tahfidz",
        "progres hafalan digital",
      ],
    },
  },

  {
    slug: "ppdb-online",
    label: "SPMB / PPDB Online",
    navDescription: "Gelombang, jalur, formulir, biaya",
    icon: FaUserPlus,
    group: "kesiswaan",
    summary:
      "Penerimaan murid baru online — gelombang, jalur, formulir, unggah berkas, sampai biaya pendaftaran.",
    detail: {
      title: "Penerimaan murid baru yang *berjalan tanpa antrean di meja*",
      lead: "Halaman pendaftaran, formulir, jadwal gelombang, dan data pendaftar dalam satu sistem",
      intro:
        "Musim penerimaan biasanya berarti tumpukan formulir kertas dan panitia yang kewalahan. Dengan SPMB online, calon murid mendaftar dari rumah lewat halaman penerimaan milik sekolah, sementara panitia memantau pendaftar yang masuk dari dashboard — bukan dari map berkas.",
      highlights: [
        "Halaman penerimaan sekolah dengan template yang bisa diatur",
        "Formulir pendaftaran standar yang dapat disesuaikan",
        "Jadwal gelombang dan data pendaftar terpantau",
      ],
      benefits: [
        {
          title: "Punya halaman penerimaan sendiri",
          body: "Sekolah mengatur beranda penerimaannya, termasuk alur pendaftaran dan template yang aktif, sehingga calon murid tahu persis harus mulai dari mana.",
        },
        {
          title: "Formulir yang bisa disesuaikan",
          body: "Pengaturan formulir standar memungkinkan sekolah menyesuaikan isian dengan kebutuhannya, tidak dipaksa memakai satu format seragam.",
        },
        {
          title: "Gelombang yang terjadwal",
          body: "Pengaturan jadwal menentukan kapan tiap gelombang dibuka dan ditutup, sehingga panitia tidak perlu mengingat dan mengumumkannya manual.",
        },
        {
          title: "Pendaftar terpantau sejak awal",
          body: "Data pendaftar masuk ke dashboard begitu formulir dikirim, memberi panitia gambaran perkembangan penerimaan setiap saat.",
        },
      ],
      screensTitle: "Yang dikerjakan panitia penerimaan",
      screensLead:
        "Siapkan halaman dan jadwalnya di awal, lalu tinggal memantau siapa yang mendaftar.",
      screens: [
        {
          src: "/image/fitur/ppdb-online/desktop-beranda.webp",
          alt: "Pengaturan beranda penerimaan murid baru dengan template aktif",
          caption: "Pengaturan Beranda",
          note: "Alur pendaftaran dan template halaman penerimaan",
          kind: "desktop",
        },
        {
          src: "/image/fitur/ppdb-online/desktop-jadwal.webp",
          alt: "Pengaturan jadwal gelombang penerimaan murid baru",
          caption: "Pengaturan Jadwal",
          note: "Pembukaan dan penutupan tiap gelombang",
          kind: "desktop",
        },
        {
          src: "/image/fitur/ppdb-online/desktop-pendaftar.webp",
          alt: "Data pendaftar penerimaan murid baru di dashboard",
          caption: "Data Pendaftar",
          note: "Pendaftar yang masuk beserta statusnya",
          kind: "desktop",
        },
      ],
      faq: [
        {
          q: "Apakah calon murid bisa mengunggah berkas persyaratan?",
          a: "Ya. Formulir pendaftaran mencakup pengisian data beserta unggahan berkas, sehingga panitia tidak perlu menerima fotokopi secara fisik.",
        },
        {
          q: "Bagaimana dengan pembayaran biaya pendaftaran?",
          a: "Biaya pendaftaran dibayarkan lewat kanal pembayaran CARDS dan terverifikasi otomatis, sehingga status pembayaran pendaftar langsung terbaca panitia.",
        },
        {
          q: "Apakah pendaftar yang diterima bisa langsung jadi siswa?",
          a: "Ya. Data pendaftar yang diterima dapat diteruskan menjadi data siswa, sehingga tidak perlu mengetik ulang di awal tahun ajaran.",
        },
      ],
      metaTitle: "Aplikasi PPDB / SPMB Online untuk Sekolah",
      metaDescription:
        "Penerimaan murid baru online: halaman penerimaan sendiri, formulir yang bisa disesuaikan, jadwal gelombang, unggah berkas, dan pembayaran terverifikasi.",
      keywords: [
        "ppdb online sekolah",
        "aplikasi spmb",
        "pendaftaran siswa baru online",
        "sistem ppdb digital",
        "penerimaan murid baru online",
      ],
    },
  },

  {
    slug: "data-anggota",
    label: "Data Anggota & Alumni",
    navDescription: "Siswa, guru, staf, alumni",
    icon: FaUsers,
    group: "kesiswaan",
    summary:
      "Basis data siswa, guru, staf, dan alumni, termasuk daftar ulang serta pengkinian data mandiri.",
    detail: {
      title: "Satu basis data yang *dipakai semua modul*",
      lead: "Siswa, guru, staf, dan alumni dalam satu sumber — termasuk daftar ulang dan pengkinian data",
      intro:
        "Presensi, tagihan, kartu, dan rapor semuanya bertumpu pada data anggota yang sama. Karena itu data siswa, guru, staf, dan alumni disimpan di satu tempat, dengan status yang bisa berubah mengikuti perjalanan mereka — dari calon murid, siswa aktif, sampai alumni.",
      highlights: [
        "Data siswa, guru, staf, dan alumni dalam satu sistem",
        "Status berubah mengikuti perjalanan, termasuk kembali jadi siswa aktif",
        "Daftar ulang dan pengkinian data mandiri oleh orang tua",
      ],
      benefits: [
        {
          title: "Tidak ada data yang berbeda-beda",
          body: "Karena semua modul membaca sumber yang sama, tidak ada lagi nama yang berbeda antara daftar presensi, tagihan, dan rapor.",
        },
        {
          title: "Alumni tetap terhubung",
          body: "Siswa yang lulus berpindah status menjadi alumni, bukan dihapus. Datanya tetap bisa dicari dan difilter saat sekolah membutuhkannya.",
        },
        {
          title: "Status bisa dikoreksi",
          body: "Bila terjadi kekeliruan, alumni dapat dikembalikan menjadi siswa aktif — perubahan status tidak bersifat satu arah yang tak bisa diperbaiki.",
        },
        {
          title: "Data dikinikan oleh yang tahu",
          body: "Pengkinian data mandiri memindahkan pembaruan alamat dan kontak ke orang tua, mengurangi beban tata usaha sekaligus menjaga data tetap akurat.",
        },
      ],
      screensTitle: "Mengelola alumni dari dashboard",
      screensLead:
        "Pencarian, filter, dan pemilihan massal — dirancang untuk data yang jumlahnya terus bertambah tiap tahun.",
      screens: [
        {
          src: "/image/fitur/data-anggota/desktop-alumni.webp",
          alt: "Daftar alumni siswa di dashboard Cards School",
          caption: "Alumni Siswa",
          note: "Pencarian, filter, dan perubahan status alumni",
          kind: "desktop",
        },
      ],
      faq: [
        {
          q: "Apa yang terjadi pada data siswa setelah lulus?",
          a: "Statusnya berubah menjadi alumni dan datanya tetap tersimpan, sehingga masih dapat dicari dan difilter — bukan dihapus dari sistem.",
        },
        {
          q: "Apakah orang tua bisa memperbarui datanya sendiri?",
          a: "Bisa. Tersedia pengaturan pengkinian data sehingga pembaruan seperti alamat dan kontak dapat dilakukan mandiri, tidak harus lewat tata usaha.",
        },
        {
          q: "Bagaimana proses daftar ulang siswa lama?",
          a: "Daftar ulang siswa lama tersedia sebagai proses tersendiri di modul anggota, sehingga her-registrasi tahunan tidak lagi memakai formulir kertas.",
        },
      ],
      metaTitle: "Data Siswa, Guru, Staf & Alumni Sekolah",
      metaDescription:
        "Satu basis data untuk siswa, guru, staf, dan alumni sekolah. Dilengkapi daftar ulang siswa lama dan pengkinian data mandiri oleh orang tua.",
      keywords: [
        "database siswa sekolah",
        "data alumni sekolah",
        "aplikasi data siswa",
        "daftar ulang siswa online",
        "pengkinian data siswa",
      ],
    },
  },

  {
    slug: "pengumuman",
    label: "Pengumuman & WhatsApp",
    navDescription: "Broadcast & buku tamu",
    icon: FaBullhorn,
    group: "kesiswaan",
    summary:
      "Sebarkan pengumuman ke orang tua lewat aplikasi dan WhatsApp, plus pencatatan buku tamu sekolah.",
    detail: {
      title: "Pengumuman yang *sampai*, bukan tenggelam di grup",
      lead: "Broadcast ke aplikasi dan WhatsApp orang tua, plus pencatatan buku tamu sekolah",
      intro:
        "Pengumuman di grup pesan cepat tertimbun obrolan lain, dan surat edaran kertas sering tidak sampai ke tangan orang tua. Modul pengumuman menyiarkan informasi resmi lewat kanal yang memang dibuka orang tua — aplikasi CARDS dan WhatsApp — sehingga sekolah punya satu jalur yang bisa diandalkan.",
      highlights: [
        "Pengumuman tampil di beranda aplikasi orang tua",
        "Penyebaran lewat WhatsApp untuk hal yang mendesak",
        "Buku tamu untuk mencatat kunjungan ke sekolah",
      ],
      benefits: [
        {
          title: "Ada di layar pertama",
          body: "Pengumuman tampil di beranda aplikasi, bukan terkubur di antara percakapan. Orang tua melihatnya saat membuka aplikasi untuk urusan lain.",
        },
        {
          title: "WhatsApp untuk yang mendesak",
          body: "Untuk informasi yang tidak boleh terlambat, penyebaran lewat WhatsApp menjangkau orang tua di kanal yang paling sering mereka buka.",
        },
        {
          title: "Satu jalur resmi",
          body: "Karena pengumuman berasal dari sekolah lewat sistem, tidak ada lagi kebingungan mana informasi resmi dan mana kabar yang beredar sendiri.",
        },
        {
          title: "Tamu sekolah tercatat",
          body: "Buku tamu digital mencatat kedatangan ke sekolah, menggantikan buku fisik di meja depan yang jarang ditelusuri kembali.",
        },
      ],
      screensTitle: "Pengumuman di beranda orang tua",
      screensLead:
        "Ditampilkan sebagai kartu bergambar, sejajar dengan informasi dan promo sekolah.",
      screens: [
        {
          src: "/image/fitur/pengumuman/crop-pengumuman.webp",
          alt: "Kartu pengumuman sekolah di beranda aplikasi CARDS",
          caption: "Pengumuman",
          note: "Terbit di beranda aplikasi orang tua",
          kind: "crop",
        },
      ],
      faq: [
        {
          q: "Apakah pengumuman bisa ditujukan ke kelas tertentu saja?",
          a: "Penargetan penerima mengikuti kebutuhan sekolah dan disesuaikan pada tahap implementasi, sehingga pengumuman tidak harus selalu ke seluruh sekolah.",
        },
        {
          q: "Apakah sekolah tahu siapa yang sudah membaca?",
          a: "Karena pengumuman terbit lewat sistem dan bukan pesan lepas, penyebarannya dapat ditelusuri. Cakupannya dibahas bersama tim kami sesuai kebutuhan Anda.",
        },
        {
          q: "Apa fungsi Buku Tamu?",
          a: "Buku tamu mencatat kedatangan tamu ke sekolah secara umum, terpisah dari modul Kunjungan yang mencatat kunjungan wali terkait siswa tertentu.",
        },
      ],
      metaTitle: "Pengumuman Sekolah via Aplikasi & WhatsApp",
      metaDescription:
        "Sebarkan pengumuman resmi sekolah ke beranda aplikasi orang tua dan WhatsApp, lengkap dengan buku tamu digital untuk mencatat kunjungan ke sekolah.",
      keywords: [
        "pengumuman sekolah online",
        "broadcast whatsapp sekolah",
        "informasi sekolah ke orang tua",
        "buku tamu digital sekolah",
        "aplikasi pengumuman sekolah",
      ],
    },
  },
  {
    slug: "multi-lembaga",
    label: "Multi Lembaga",
    navDescription: "Banyak unit dalam satu akun yayasan",
    icon: FaSitemap,
    group: "kesiswaan",
    summary:
      "Kelola beberapa sekolah atau unit pesantren dari satu akun — data tiap lembaga terpisah, pantauan yayasan menyatu.",
    detail: {
      title: "Satu akun untuk *seluruh unit yayasan*",
      lead: "SD, SMP, SMA, sampai pondok — tiap lembaga berjalan dengan datanya sendiri, yayasan memantau dari satu pintu",
      intro:
        "Yayasan dengan banyak unit biasanya berarti banyak sistem, banyak login, dan laporan yang tidak pernah seragam. Di CARDS, setiap lembaga berdiri dengan data, tahun ajaran, dan keuangannya masing-masing — sementara pengurus yayasan mengelolanya dari satu akun yang sama.",
      highlights: [
        "Beberapa lembaga dikelola dari satu akun",
        "Tahun ajaran, data, dan keuangan terpisah per lembaga",
        "Saldo CazhBox per instansi, dengan alur dana pusat ↔ unit",
      ],
      benefits: [
        {
          title: "Satu login, semua unit",
          body: "Pengurus yayasan tidak berpindah-pindah sistem: seluruh unit di bawah naungannya dikelola dari akun yang sama.",
        },
        {
          title: "Data tiap unit tidak tercampur",
          body: "Setiap lembaga memiliki tahun ajaran aktif, data anggota, dan pencatatan keuangannya sendiri — otonomi unit tetap terjaga.",
        },
        {
          title: "Keuangan pusat dan unit terhubung",
          body: "Saldo CazhBox tercatat per instansi; dana dapat dipindahkan antara pusat dan unit, diisi melalui virtual account, atau ditarik ke rekening lembaga — semuanya berjejak.",
        },
        {
          title: "Laporan yang akhirnya seragam",
          body: "Karena semua unit memakai sistem yang sama, yayasan membandingkan kondisi antar lembaga dari data berformat sama, bukan menyatukan laporan yang berbeda-beda.",
        },
      ],
      screensTitle: "Cara kerja satu akun banyak lembaga",
      screensLead:
        "Dari pendaftaran unit sampai pemantauan lintas lembaga oleh yayasan.",
      steps: [
        {
          title: "Daftarkan unit-unitnya",
          body: "Setiap sekolah atau pondok di bawah yayasan didaftarkan sebagai lembaga dengan profilnya sendiri.",
        },
        {
          title: "Tiap unit berjalan mandiri",
          body: "Admin tiap lembaga mengelola tahun ajaran, anggota, presensi, dan tagihannya sendiri tanpa mengganggu unit lain.",
        },
        {
          title: "Dana mengalir tercatat",
          body: "CazhBox per instansi menampung dananya masing-masing; perpindahan pusat ke unit maupun sebaliknya terekam dengan riwayatnya.",
        },
        {
          title: "Yayasan memantau dari satu pintu",
          body: "Pengurus pusat membuka kondisi tiap unit — dari keuangan sampai operasional — tanpa meminta laporan manual.",
        },
      ],
      faq: [
        {
          q: "Apakah tiap unit bisa punya kebijakan berbeda?",
          a: "Ya. Pengaturan seperti tahun ajaran, jenis tagihan, dan jadwal presensi berlaku per lembaga, sehingga tiap unit mengikuti kebiasaannya sendiri.",
        },
        {
          q: "Bagaimana pembagian akses antara yayasan dan admin unit?",
          a: "Hak akses diatur lewat pengaturan pengguna: admin unit mengelola lembaganya sendiri, sementara akun yayasan menjangkau seluruh unit di bawahnya.",
        },
        {
          q: "Bisakah dana dipindahkan antar unit?",
          a: "Perpindahan dana berjalan melalui CazhBox pusat: unit menarik ke pusat atau pusat mengisi ke unit, dan setiap perpindahan tercatat pada riwayat transaksinya.",
        },
      ],
      metaTitle: "Manajemen Multi Lembaga untuk Yayasan",
      metaDescription:
        "Kelola banyak sekolah dan unit pesantren dari satu akun yayasan. Data dan keuangan terpisah per lembaga, saldo per instansi, dan pemantauan pusat yang menyatu.",
      keywords: [
        "manajemen multi cabang sekolah",
        "dashboard yayasan",
        "aplikasi yayasan pendidikan",
        "manajemen multi unit pesantren",
        "sistem informasi yayasan",
      ],
    },
  },
];
